import React, { useState, type ReactNode, useEffect, useCallback, memo } from 'react';
import { cssMap, styled } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import ShareIconNew from '@atlaskit/icon/core/share';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Box } from '@atlaskit/primitives/compiled';
import type { TooltipPosition } from '@atlassian/jira-share-dialog/src/common/types.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIsSplitShareButtonEnabled } from '../../common/hooks/use-is-split-share-button-enabled.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import { WarningContent } from './warning-content/index.tsx';

const styles = cssMap({
	dialogLayout: {
		textAlign: 'center',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

const ConditionalTooltip = ({
	children,
	position,
}: {
	children: ReactNode;
	position?: TooltipPosition;
}) => {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={formatMessage(messages.shareTriggerButtonText)}
			{...(fg('jfp_a11y_team_button_has_verbose_accessible_name') && {
				isScreenReaderAnnouncementDisabled: true,
			})}
			position={fg('plan-timeline-share-tooltip-position') ? (position ?? 'top') : 'top'}
		>
			{children}
		</Tooltip>
	);
};

export const PreShareView = memo(
	({
		triggerButtonStyle,
		loading,
		error,
		emailSettings,
		hipaaStatus,
		renderCustomTriggerButton,
		customTriggerButtonIcon,
		getEmailSettings,
		getHipaaStatus,
		getUserPermission,
		getUserRoles,
		isAutoOpenDialog = false,
		onDialogClose,
		onTriggerButtonClick,
		shareShortcutTriggerCount,
		poSpotlightShareButtonId,
		triggerButtonTooltipPosition,
		placement = fg('move_share_dialog_to_jira') ? 'bottom-end' : 'left-start',
	}: Props) => {
		const { formatMessage } = useIntl();
		const [isOpen, setIsOpen] = useState(isAutoOpenDialog);
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const isSplitShareButtonEnabled = useIsSplitShareButtonEnabled();

		// Used to invoke a re-render when isAutoOpenDialog has not changed but the shortcut has been pressed again.
		useEffect(() => {
			setIsOpen(isAutoOpenDialog);
		}, [isAutoOpenDialog, shareShortcutTriggerCount]);

		const onTriggerClick = useCallback(
			(trigger: 'PreShareView' | 'RenderCustomTriggerPreShareView') => {
				const analyticsEvent = createAnalyticsEvent({
					action: 'PreShareView Button Clicked',
					actionSubject: 'Share',
				});
				setIsOpen(!isOpen);
				if (!isOpen) {
					getUserPermission();
					getEmailSettings();
					getHipaaStatus();
					expVal('tmp_secure_project_access_share_dialog', 'isEnabled', false) && getUserRoles?.();
				}
				fireUIAnalytics(analyticsEvent, trigger);
				if (onTriggerButtonClick) {
					onTriggerButtonClick();
				}
			},
			[
				createAnalyticsEvent,
				isOpen,
				onTriggerButtonClick,
				getUserPermission,
				getEmailSettings,
				getHipaaStatus,
				getUserRoles,
			],
		);

		return (
			<Popup
				onClose={() => {
					setIsOpen(false);
					onDialogClose && onDialogClose();
				}}
				zIndex={layers.modal}
				messageId="share-button.ui.pre-share-view.popup"
				messageType="transactional"
				content={() => (
					<ShortcutScope>
						{fg('jira-warepil-migrate-motion-to-complied') ? (
							<Box xcss={styles.dialogLayout} testId="share-button.ui.pre-share-view.warning-modal">
								{loading ? (
									<Spinner testId="share-button.ui.pre-share-view.loading" size="medium" />
								) : (
									<WarningContent
										isEmailEnabled={emailSettings?.isEmailEnabled}
										canUserEnableEmail={!!emailSettings?.canUserEnableEmail}
										isUserHIPAA={!!hipaaStatus?.isCompliant}
									/>
								)}
								{error && (
									// eslint-disable-next-line @atlaskit/design-system/use-heading
									<h1>
										<FormattedMessage {...messages.errorMessage} />
									</h1>
								)}
							</Box>
						) : (
							<DialogLayout data-testid="share-button.ui.pre-share-view.warning-modal">
								{loading ? (
									<Spinner testId="share-button.ui.pre-share-view.loading" size="medium" />
								) : (
									<WarningContent
										isEmailEnabled={emailSettings?.isEmailEnabled}
										canUserEnableEmail={!!emailSettings?.canUserEnableEmail}
										isUserHIPAA={!!hipaaStatus?.isCompliant}
									/>
								)}
								{error && (
									// eslint-disable-next-line @atlaskit/design-system/use-heading
									<h1>
										<FormattedMessage {...messages.errorMessage} />
									</h1>
								)}
							</DialogLayout>
						)}
					</ShortcutScope>
				)}
				isOpen={isOpen}
				placement={
					fg('empanada_nin_covnert_share_to_entrypoint') || fg('move_share_dialog_to_jira')
						? placement
						: 'left-start'
				}
				trigger={(triggerProps) =>
					renderCustomTriggerButton ? (
						renderCustomTriggerButton(
							{
								isDisabled: loading,
								isSelected: isOpen,
								onClick: () => onTriggerClick('RenderCustomTriggerPreShareView'),
								...(isSplitShareButtonEnabled && { iconBefore: customTriggerButtonIcon }),
							},
							triggerProps,
						)
					) : (
						<ConditionalTooltip
							position={
								fg('plan-timeline-share-tooltip-position')
									? triggerButtonTooltipPosition
									: undefined
							}
						>
							<UFOSegment name="issue-pre-share-button">
								<IconButton
									testId="share-button.ui.pre-share-view.button"
									id={poSpotlightShareButtonId}
									isDisabled={loading}
									label={formatMessage(messages.shareTriggerButtonText)}
									{...triggerProps}
									appearance="default"
									icon={() => (
										<ShareIconNew
											spacing="spacious"
											{...(fg('jfp_a11y_team_button_has_verbose_accessible_name')
												? {
														testId: 'share-button.ui.pre-share-view.share-icon',
														label: '',
													}
												: {
														label:
															triggerButtonStyle === 'icon-with-text'
																? ''
																: formatMessage(messages.shareTriggerButtonText),
													})}
											color={token('color.icon')}
										/>
									)}
									onClick={() => onTriggerClick('PreShareView')}
									interactionName="share-button.pre-share-view"
								/>
							</UFOSegment>
						</ConditionalTooltip>
					)
				}
			/>
		);
	},
);

export default PreShareView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogLayout = styled.div<{
	children?: ReactNode;
	['data-testid']: string;
}>({
	textAlign: 'center',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});
